import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SlideTrack from "../../../components/Common/SlideTrack";
import { useSlideLock } from "../../../hooks/useSlideLock";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startAddContentsToList } from "../../../actions/user";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  BUTTON_NEXT,
  BUTTON_PREVIOUS,
  BUTTON_SAVE,
  TEMPLATES_EDITOR_PREVIEW_SAVED_TO_GALLERY,
  TEMPLATES_EDITOR_PREVIEW_TITLE,
  TEMPLATES_EDITOR_PREVIEW_USED_VALUES,
  TEMPLATES_PRODUCE_SAVE_ERROR,
} from "../../../i18n/keysTranslations";
import { FIELD_PLACEHOLDER_COLOR } from "../../../utils/colors";
import { produceFieldLabelSx } from "../produce/produceCardStyles";

const EMPTY_VALUE = "—";

const PreviewVariableValue = ({ entry }) => {
  const type = entry?.type === "image" ? "image" : "string";
  const value = String(entry?.value ?? "").trim();

  switch (type) {
    case "image": {
      if (!value) {
        return (
          <Typography variant="body2" color="text.secondary">
            {EMPTY_VALUE}
          </Typography>
        );
      }
      return (
        <Box
          component="img"
          src={value}
          alt={entry?.label || ""}
          sx={{
            display: "block",
            width: 72,
            height: 72,
            objectFit: "cover",
            borderRadius: "8px",
            backgroundColor: "#EEE",
          }}
        />
      );
    }
    case "string":
      return (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: value ? "text.primary" : "text.secondary",
          }}
        >
          {value || EMPTY_VALUE}
        </Typography>
      );
    default: {
      const exhaustive = type;
      throw new Error(`Unhandled preview variable type: ${exhaustive}`);
    }
  }
};

const listPreviewImageUrls = ({ url = "", urls = [] } = {}) => {
  const source = Array.isArray(urls) && urls.length > 0 ? urls : [url];
  return source.map((item) => String(item || "").trim()).filter(Boolean);
};

const PreviewImageActions = ({ urls = [] }) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [isSaved, setIsSaved] = useState(false);
  const imageKey = urls.join("\n");

  useEffect(() => {
    setIsSaved(false);
  }, [imageKey]);

  const onSaveToGallery = useCallback(async () => {
    if (urls.length === 0 || isSaved) return;
    const [firstUrl] = urls;
    const payload =
      urls.length > 1
        ? {
            url: firstUrl,
            type: "upload",
            isAIGenerated: true,
            isCarousel: true,
            carouselImages: urls.map((imageUrl, index) => ({
              url: imageUrl,
              id: String(index),
            })),
          }
        : {
            url: firstUrl,
            type: "upload",
            isAIGenerated: true,
          };

    const saveResult = await dispatch(startAddContentsToList([payload]));
    if (!saveResult) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
        icon: "error",
      });
      return;
    }
    setIsSaved(true);
    SimpleAlert({
      title: t(TEMPLATES_EDITOR_PREVIEW_SAVED_TO_GALLERY),
      icon: "success",
    });
  }, [dispatch, isSaved, t, urls]);

  return (
    <ContainerModalActions
      hasPadding={false}
      propsPrimaryButton={{
        showButton: true,
        text: BUTTON_SAVE,
        onClick: onSaveToGallery,
        disabled: urls.length === 0 || isSaved,
      }}
    />
  );
};

const TemplateEditorPreviewImageModal = ({
  open = false,
  url = "",
  urls = [],
  variables = [],
  onClose = () => {},
  showSave = true,
}) => {
  const { t } = useTranslationApp();
  const images = listPreviewImageUrls({ url, urls });
  const hasVariables = variables.length > 0;
  const [imageIndex, setImageIndex] = useState(0);
  const imageKey = images.join("\n");
  const activeUrl = images[imageIndex] || images[0] || "";
  const hasManyImages = images.length > 1;
  const isFirstImage = imageIndex === 0;
  const isLastImage = imageIndex === images.length - 1;

  useEffect(() => {
    setImageIndex(0);
  }, [imageKey]);

  const { isSlideLocked, lockSlide } = useSlideLock();

  const onShowPreviousImage = () => {
    if (isFirstImage || !lockSlide()) return;
    setImageIndex((current) => current - 1);
  };

  const onShowNextImage = () => {
    if (isLastImage || !lockSlide()) return;
    setImageIndex((current) => current + 1);
  };

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onClose}
      title={t(TEMPLATES_EDITOR_PREVIEW_TITLE)}
      ariaLabel={t(TEMPLATES_EDITOR_PREVIEW_TITLE)}
      maxWidth={hasVariables ? "lg" : "md"}
      fullWidth
      sxContent={
        hasVariables
          ? {
              overflow: { xs: "auto", sm: "hidden" },
              display: "flex",
              flexDirection: "column",
            }
          : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: { xs: 2, sm: 3 },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <SlideTrack
            urls={images}
            index={imageIndex}
            sx={{ borderRadius: "12px", backgroundColor: "#F4F4F5" }}
            sizer={
              <Box
                component="img"
                src={activeUrl}
                alt=""
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  width: "auto",
                  height: "auto",
                  visibility: "hidden",
                }}
              />
            }
            renderSlide={(slideUrl, slideIndex) => (
              <Box
                component="img"
                src={slideUrl}
                alt={`${t(TEMPLATES_EDITOR_PREVIEW_TITLE)} ${slideIndex + 1}`}
                sx={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          />
          {hasManyImages ? (
            <>
              <IconButton
                aria-label={t(BUTTON_PREVIOUS)}
                onClick={onShowPreviousImage}
                disabled={isFirstImage || isSlideLocked}
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  "&:hover": {
                    bgcolor: isFirstImage
                      ? "rgba(0, 0, 0, 0.7)"
                      : "rgba(0, 0, 0, 0.9)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ color: "#fff !important" }} />
              </IconButton>
              <IconButton
                aria-label={t(BUTTON_NEXT)}
                onClick={onShowNextImage}
                disabled={isLastImage || isSlideLocked}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  "&:hover": {
                    bgcolor: isLastImage
                      ? "rgba(0, 0, 0, 0.7)"
                      : "rgba(0, 0, 0, 0.9)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    bgcolor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                <ChevronRightIcon sx={{ color: "#fff !important" }} />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#fff !important",
                }}
              >
                {imageIndex + 1} / {images.length}
              </Box>
            </>
          ) : null}
        </Box>
        {hasVariables ? (
          <Box
            sx={{
              width: { xs: "100%", sm: 280 },
              flexShrink: 0,
              minWidth: 0,
              maxHeight: { xs: 240, sm: "70vh" },
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              {t(TEMPLATES_EDITOR_PREVIEW_USED_VALUES)}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {variables.map((entry) => (
                <Box
                  key={entry.key}
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography
                    sx={{
                      ...produceFieldLabelSx,
                      color: FIELD_PLACEHOLDER_COLOR,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {entry.label}
                  </Typography>
                  <PreviewVariableValue entry={entry} />
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>
      {showSave ? (
        <DialogActions
          sx={{
            px: {
              xs: 3,
              sm: 5,
            },
            pb: 3,
            mt: 0.5,
          }}
        >
          <PreviewImageActions urls={images} />
        </DialogActions>
      ) : null}
    </ModalBasicLayout>
  );
};

export default TemplateEditorPreviewImageModal;
